<script>
  import { SEARCH_SORT_OPTIONS } from '../../lib/logic/config.js';

  let {
    viewMode = 'list',
    sortMode = 'name-asc',
    filtersOpen = false,
    activeFilterCount = 0,
    initialQuery = '',
    searchPlaceholder = 'Search cards across your binders...',
    // Search opts into autofocus (the query box is the page's primary action);
    // the want list does not (its filter box is secondary). Review §9e.
    autofocus = false,
    onInput,
    onViewChange,
    onSortChange,
    onToggleFilters,
  } = $props();
</script>

<div class="search-page-header">
  <div class="search-input-wrapper">
    <svg
      class="search-input-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
    >
      <circle cx="11" cy="11" r="8"></circle>
      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
    </svg>
    <!-- svelte-ignore a11y_autofocus -->
    <input
      type="text"
      class="search-input"
      placeholder={searchPlaceholder}
      {autofocus}
      value={initialQuery}
      oninput={(e) => onInput(e.currentTarget.value.trim())}
    />
  </div>

  <div class="search-controls">
    <!-- Filter toggle -->
    <button
      class="search-control-btn"
      class:active={filtersOpen}
      title="Filters"
      onclick={onToggleFilters}
    >
      <svg
        viewBox="0 0 24 24"
        width="18"
        height="18"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <line x1="4" y1="6" x2="20" y2="6"></line>
        <line x1="7" y1="12" x2="17" y2="12"></line>
        <line x1="10" y1="18" x2="14" y2="18"></line>
      </svg>
      {#if activeFilterCount > 0}
        <span class="filter-badge">{activeFilterCount}</span>
      {/if}
    </button>

    <!-- Sort dropdown -->
    <select
      class="sort-select"
      value={sortMode}
      onchange={(e) => onSortChange(e.currentTarget.value)}
      title="Sort results"
    >
      {#each SEARCH_SORT_OPTIONS as opt (opt.value)}
        <option value={opt.value}>{opt.label}</option>
      {/each}
    </select>

    <!-- View toggle -->
    <div class="search-view-toggle">
      <button
        class="search-view-btn"
        class:active={viewMode === 'list'}
        title="List view"
        onclick={() => onViewChange('list')}
      >
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <line x1="8" y1="6" x2="21" y2="6"></line>
          <line x1="8" y1="12" x2="21" y2="12"></line>
          <line x1="8" y1="18" x2="21" y2="18"></line>
          <line x1="3" y1="6" x2="3.01" y2="6"></line>
          <line x1="3" y1="12" x2="3.01" y2="12"></line>
          <line x1="3" y1="18" x2="3.01" y2="18"></line>
        </svg>
      </button>
      <button
        class="search-view-btn"
        class:active={viewMode === 'grid'}
        title="Grid view"
        onclick={() => onViewChange('grid')}
      >
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <rect x="3" y="3" width="7" height="7"></rect>
          <rect x="14" y="3" width="7" height="7"></rect>
          <rect x="3" y="14" width="7" height="7"></rect>
          <rect x="14" y="14" width="7" height="7"></rect>
        </svg>
      </button>
    </div>
  </div>
</div>

<style>
  .search-page-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 0;
  }

  .search-input-wrapper {
    flex: 1;
    position: relative;
  }

  .search-input-icon {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--color-text-placeholder);
    width: 16px;
    height: 16px;
    pointer-events: none;
  }

  .search-input {
    width: 100%;
    padding: 0.5rem 1rem 0.5rem 2.5rem;
    border: 1px solid var(--color-border-subtle);
    border-radius: 0.5rem;
    font-size: 0.875rem;
    font-family: var(--font-sans);
    background: var(--color-bg-surface);
    outline: none;
    transition:
      border-color 0.2s,
      box-shadow 0.2s;
    box-sizing: border-box;
  }

  .search-input:focus {
    border-color: var(--color-text-tertiary);
    box-shadow: 0 0 0 3px rgba(154, 142, 124, 0.1);
  }

  .search-controls {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    flex-shrink: 0;
  }

  .search-control-btn {
    position: relative;
    padding: 0.375rem 0.5rem;
    border: 1px solid var(--color-border-subtle);
    background: var(--color-bg-surface);
    border-radius: 0.375rem;
    cursor: pointer;
    color: var(--color-text-tertiary);
    transition:
      background var(--dur-fast) var(--ease-standard),
      border-color var(--dur-fast) var(--ease-standard),
      color var(--dur-fast) var(--ease-standard);
    display: flex;
    align-items: center;
  }

  .search-control-btn:hover {
    background: var(--color-accent-bg);
    border-color: var(--color-text-muted);
    color: var(--color-text);
  }

  .search-control-btn.active {
    background: var(--color-accent-bg);
    border-color: var(--color-text-tertiary);
    color: var(--color-text);
  }

  .filter-badge {
    position: absolute;
    top: -6px;
    right: -6px;
    background: var(--color-accent-dark);
    color: white;
    font-size: 0.625rem;
    font-weight: 700;
    min-width: 16px;
    height: 16px;
    border-radius: 9999px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 3px;
    line-height: 1;
  }

  .sort-select {
    padding: 0.375rem 0.5rem;
    border: 1px solid var(--color-border-subtle);
    border-radius: 0.375rem;
    font-size: 0.75rem;
    font-family: inherit;
    color: var(--color-text);
    background: var(--color-bg-surface);
    cursor: pointer;
    outline: none;
    transition:
      border-color 0.15s,
      box-shadow 0.15s;
  }

  .sort-select:focus {
    border-color: var(--color-text-tertiary);
    box-shadow: 0 0 0 2px rgba(154, 142, 124, 0.1);
  }

  .search-view-toggle {
    display: flex;
    gap: 0.25rem;
    background: var(--color-accent-bg);
    border-radius: 0.375rem;
    padding: 2px;
  }

  .search-view-btn {
    padding: 0.375rem 0.5rem;
    border: none;
    background: transparent;
    border-radius: 0.25rem;
    cursor: pointer;
    color: var(--color-text-tertiary);
    transition:
      background var(--dur-fast) var(--ease-standard),
      color var(--dur-fast) var(--ease-standard),
      box-shadow var(--dur-fast) var(--ease-standard);
    display: flex;
    align-items: center;
  }

  .search-view-btn.active {
    background: var(--color-bg-surface);
    color: var(--color-text);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  .search-view-btn:hover:not(.active) {
    color: var(--color-text);
  }

  @media (min-width: 769px) {
    .search-input {
      max-width: 500px;
      font-size: 1rem;
    }
  }

  @media (min-width: 1024px) {
    .search-input {
      max-width: 600px;
    }
  }

  @media (max-width: 768px) {
    .search-page-header {
      gap: 0.375rem;
    }

    .sort-select {
      display: none;
    }

    .search-view-toggle {
      display: none;
    }
  }
</style>
